import type { GlossaryTerm } from './types';

/**
 * Terms cover the five modules shipped in PR 2 (onboarding, customers,
 * contracts, catalog, calendar). Terms that only make sense once Pedidos and
 * Producción exist (cierre de corte, solicitud de cambio, anexo,
 * consolidado, contaminación cruzada, override de corte) are added in PR 3,
 * so no entry here references a `modules` id that does not exist yet.
 */
export const GLOSSARY_TERMS: GlossaryTerm[] = [
  {
    id: 'sesion',
    term: 'Sesión',
    definition:
      'El período en el que estás conectado al sistema, desde que iniciás sesión hasta que cerrás sesión o el sistema te desconecta por seguridad.',
    modules: ['onboarding'],
  },
  {
    id: 'correo-electronico',
    term: 'Correo electrónico',
    definition:
      'El email con el que iniciás sesión. Es el mismo que usa el administrador para darte de alta como usuario.',
    modules: ['onboarding'],
  },
  {
    id: 'contrasena',
    term: 'Contraseña',
    definition: 'La clave secreta que usás junto con tu correo electrónico para iniciar sesión.',
    modules: ['onboarding'],
  },
  {
    id: 'verificacion-en-dos-pasos',
    term: 'Verificación en dos pasos',
    definition:
      'Una capa extra de seguridad: además de tu contraseña, tenés que ingresar un código de seis dígitos que genera una aplicación de autenticación en tu teléfono.',
    relatedTerms: ['codigo-qr'],
    modules: ['onboarding'],
  },
  {
    id: 'codigo-qr',
    term: 'Código QR',
    definition:
      'La imagen que escaneás con tu aplicación de autenticación para vincularla a tu cuenta y activar la verificación en dos pasos.',
    relatedTerms: ['verificacion-en-dos-pasos'],
    modules: ['onboarding'],
  },
  {
    id: 'empresa',
    term: 'Empresa',
    definition:
      'El cliente registrado en el sistema: la razón social que contrata el servicio de catering, con una o más sedes.',
    relatedTerms: ['sede', 'contrato'],
    modules: ['customers', 'contracts'],
  },
  {
    id: 'sede',
    term: 'Sede',
    definition:
      'Una ubicación física de una empresa donde se entrega el servicio: una oficina, una planta, un edificio. Una empresa puede tener varias sedes.',
    relatedTerms: ['empresa', 'sede-por-defecto', 'ventana-de-entrega'],
    modules: ['customers'],
  },
  {
    id: 'sede-por-defecto',
    term: 'Sede por defecto',
    definition:
      'La sede principal de una empresa. No se puede desactivar ni eliminar hasta que otra sede pase a ser la sede por defecto.',
    relatedTerms: ['sede'],
    modules: ['customers'],
  },
  {
    id: 'ventana-de-entrega',
    term: 'Ventana horaria',
    definition:
      'El rango horario en el que una sede puede recibir entregas, por ejemplo de 08:00 a 12:00.',
    relatedTerms: ['sede'],
    modules: ['customers'],
  },
  {
    id: 'contacto',
    term: 'Contacto',
    definition:
      'Una persona vinculada a una empresa o a una de sus sedes, con su email y teléfono, que puede recibir notificaciones o tener usuario del portal.',
    relatedTerms: ['corporativo'],
    modules: ['customers'],
  },
  {
    id: 'corporativo',
    term: 'Corporativo',
    definition: 'Un contacto que pertenece a la empresa en general, no a una sede en particular.',
    relatedTerms: ['contacto'],
    modules: ['customers'],
  },
  {
    id: 'acceso-al-portal',
    term: 'Acceso al portal',
    definition:
      'El permiso que le das a un contacto de la empresa para iniciar sesión en el portal del cliente, con un rol y una o varias sedes asignadas. Se crea invitando a un contacto desde la pestaña "Accesos al portal" y queda pendiente hasta que el contacto acepta la invitación.',
    relatedTerms: ['contacto'],
    modules: ['customers'],
  },
  {
    id: 'suspension',
    term: 'Suspensión',
    definition:
      'Un estado temporal que se le da a una empresa cuando deja de operar por un tiempo, con un motivo obligatorio. Se puede reactivar cuando corresponda.',
    relatedTerms: ['reactivacion', 'baja'],
    modules: ['customers'],
  },
  {
    id: 'reactivacion',
    term: 'Reactivación',
    definition: 'La acción de volver a habilitar una empresa que estaba suspendida.',
    relatedTerms: ['suspension'],
    modules: ['customers'],
  },
  {
    id: 'baja',
    term: 'Dar de baja',
    definition:
      'Terminar de forma definitiva el vínculo con una empresa o un contrato. En empresas, solo se puede revertir desde soporte; en contratos, se pide un motivo.',
    relatedTerms: ['suspension'],
    modules: ['customers', 'contracts'],
  },
  {
    id: 'zona',
    term: 'Zona',
    definition:
      'El área geográfica a la que pertenece una empresa o un feriado. Sirve para filtrar y para que los feriados apliquen solo donde corresponde.',
    relatedTerms: ['feriado-de-zona'],
    modules: ['customers', 'calendar'],
  },
  {
    id: 'contrato',
    term: 'Contrato',
    definition:
      'El acuerdo comercial con una empresa: define precios, condiciones de facturación, topes de cantidad y hora de corte. Cada contrato tiene un código y pertenece a una sola empresa.',
    relatedTerms: ['version-de-contrato', 'vigencia'],
    modules: ['contracts'],
  },
  {
    id: 'vigencia',
    term: 'Vigencia',
    definition:
      'El período durante el cual un contrato está en efecto, desde su fecha de inicio hasta su fecha de fin (si la tiene).',
    relatedTerms: ['contrato'],
    modules: ['contracts'],
  },
  {
    id: 'version-de-contrato',
    term: 'Versión de contrato',
    definition:
      'Cada vez que se edita un contrato activo, el sistema genera una nueva versión con el mismo código. El historial de versiones muestra todas las versiones y cuál reemplazó a cuál.',
    relatedTerms: ['contrato', 'contrato-vencido'],
    modules: ['contracts'],
  },
  {
    id: 'ciclo-de-facturacion',
    term: 'Ciclo de facturación',
    definition: 'La frecuencia con la que se factura un contrato, por ejemplo mensual o quincenal.',
    modules: ['contracts'],
  },
  {
    id: 'plazo-de-pago',
    term: 'Plazo de pago',
    definition:
      'La cantidad de días que tiene la empresa para pagar una factura después de emitida.',
    modules: ['contracts'],
  },
  {
    id: 'corte',
    term: 'Corte',
    definition:
      'El momento en que se deja de aceptar cambios para una fecha de servicio. Cada contrato define su propia hora de corte.',
    relatedTerms: ['hora-de-corte'],
    modules: ['contracts'],
  },
  {
    id: 'hora-de-corte',
    term: 'Hora de corte',
    definition:
      'El horario límite hasta el que se pueden confirmar pedidos para una fecha de servicio. Cada contrato define su propia hora de corte efectiva.',
    relatedTerms: ['contrato'],
    modules: ['contracts'],
  },
  {
    id: 'dias-de-anticipacion-de-corte',
    term: 'Días de anticipación de corte',
    definition:
      'Cuántos días antes de la fecha de servicio se aplica la hora de corte del contrato.',
    relatedTerms: ['hora-de-corte'],
    modules: ['contracts'],
  },
  {
    id: 'tope-diario',
    term: 'Tope diario',
    definition:
      'La cantidad mínima o máxima de unidades por día que permite un contrato. Un pedido por debajo del mínimo se marca "Bajo el mínimo" en el Tablero del día.',
    modules: ['contracts', 'orders'],
  },
  {
    id: 'dias-de-servicio',
    term: 'Días de servicio',
    definition: 'Los días de la semana en los que un contrato tiene servicio habilitado.',
    modules: ['contracts'],
  },
  {
    id: 'lista-de-precios',
    term: 'Lista de precios',
    definition:
      'El conjunto de precios que un contrato tiene asignados: un precio por servicio por cada tipo (almuerzo, cena) y, opcionalmente, excepciones por plato puntual, cada uno con su alícuota de IVA y su vigencia.',
    relatedTerms: ['precio-por-servicio', 'excepcion-por-plato', 'actualizacion-masiva', 'alicuota-de-iva'],
    modules: ['contracts'],
  },
  {
    id: 'precio-por-servicio',
    term: 'Precio por servicio',
    definition:
      'El precio base de un contrato para un tipo de servicio (por ejemplo almuerzo o cena), aplicado a cualquier plato de ese tipo salvo que exista una excepción por plato. Sin un precio por servicio vigente, el contrato no se puede activar.',
    relatedTerms: ['lista-de-precios', 'excepcion-por-plato', 'tipo-de-servicio'],
    modules: ['contracts'],
  },
  {
    id: 'precio-de-catalogo',
    term: 'Precio de catálogo',
    definition:
      'El precio base de un plato en el catálogo de menús. En un pedido solo se usa como respaldo, cuando el contrato no tiene ni una excepción por plato ni un precio por servicio para ese plato; se factura con la alícuota de IVA por defecto. Un plato o servicio marcado como "no disponible" en el contrato no cae al precio de catálogo.',
    relatedTerms: ['precio-por-servicio', 'excepcion-por-plato', 'lista-de-precios'],
    modules: ['contracts', 'orders'],
  },
  {
    id: 'excepcion-por-plato',
    term: 'Excepción por plato',
    definition:
      'Un precio distinto al precio por servicio, cargado para un plato puntual. Cuando existe una excepción vigente para ese plato, gana sobre el precio por servicio.',
    relatedTerms: ['lista-de-precios', 'precio-por-servicio'],
    modules: ['contracts'],
  },
  {
    id: 'actualizacion-masiva',
    term: 'Actualización masiva',
    definition:
      'Una forma de aumentar o descontar un porcentaje a todos los precios de un contrato de una sola vez, tanto los precios por servicio como las excepciones por plato. Siempre muestra una previsualización antes de aplicar el cambio.',
    relatedTerms: ['lista-de-precios', 'precio-por-servicio', 'excepcion-por-plato'],
    modules: ['contracts'],
  },
  {
    id: 'alicuota-de-iva',
    term: 'Alícuota de IVA',
    definition:
      'El porcentaje de IVA que se aplica al precio de un menú dentro de un contrato. Se elige de un catálogo administrable en Configuración → Alícuotas de IVA, que también define cuál se propone por defecto.',
    relatedTerms: ['lista-de-precios'],
    modules: ['contracts'],
  },
  {
    id: 'documento-del-contrato',
    term: 'Documento del contrato',
    definition:
      'El archivo PDF firmado que respalda al contrato, cargado desde la pestaña Documento.',
    modules: ['contracts'],
  },
  {
    id: 'contrato-borrador',
    term: 'Borrador (contrato)',
    definition:
      'El contrato todavía no fue activado. Se puede editar libremente antes de activarlo.',
    relatedTerms: ['contrato-activo'],
    modules: ['contracts'],
  },
  {
    id: 'contrato-activo',
    term: 'Activo (contrato)',
    definition: 'El contrato está vigente y en uso. Editarlo genera una nueva versión.',
    relatedTerms: ['contrato-borrador', 'contrato-pausado'],
    modules: ['contracts'],
  },
  {
    id: 'contrato-pausado',
    term: 'Pausado (contrato)',
    definition: 'El contrato está temporalmente sin efecto, pero se puede reactivar.',
    relatedTerms: ['contrato-activo'],
    modules: ['contracts'],
  },
  {
    id: 'contrato-vencido',
    term: 'Vencido (contrato)',
    definition:
      'El contrato llegó a su fecha de fin. Si fue reemplazado por una versión nueva, el historial de versiones lo indica.',
    relatedTerms: ['version-de-contrato'],
    modules: ['contracts'],
  },
  {
    id: 'contrato-dado-de-baja',
    term: 'Dado de baja (contrato)',
    definition:
      'El contrato fue terminado de forma definitiva, con un motivo obligatorio. No se puede revertir desde la pantalla de contratos.',
    relatedTerms: ['baja'],
    modules: ['contracts'],
  },
  {
    id: 'menu',
    term: 'Menú',
    definition:
      'Un plato o combinación de platos que se puede ofrecer y pedir. Tiene un código, un precio base, un tipo de servicio y puede tener etiquetas dietarias y alérgenos.',
    relatedTerms: ['tipo-de-servicio', 'etiqueta-dietaria', 'alergeno'],
    modules: ['catalog'],
  },
  {
    id: 'tipo-de-servicio',
    term: 'Tipo de servicio',
    definition:
      'La categoría de un menú según el momento del día en que se sirve, por ejemplo desayuno, almuerzo, vianda o cena.',
    relatedTerms: ['menu'],
    modules: ['catalog'],
  },
  {
    id: 'etiqueta-dietaria',
    term: 'Etiqueta dietaria',
    definition:
      'Una marca que indica que un menú cumple con una condición alimentaria, por ejemplo sin TACC o vegano.',
    relatedTerms: ['menu', 'alergeno'],
    modules: ['catalog'],
  },
  {
    id: 'alergeno',
    term: 'Alérgeno',
    definition:
      'Un ingrediente que un menú contiene y que puede provocar una reacción alérgica, por ejemplo maní o mariscos. Se declara para que quien pide pueda evitarlo.',
    relatedTerms: ['menu', 'etiqueta-dietaria'],
    modules: ['catalog'],
  },
  {
    id: 'posicion-del-menu',
    term: 'Posición del menú',
    definition:
      'Un lugar fijo dentro de un tipo de servicio, por ejemplo "Almuerzo general" u "Opción de cena". El calendario, los pedidos y las plantillas se arman por posición: cada día, cada posición activa recibe un plato del tipo de servicio que le corresponde. Una posición sin uso se desactiva, nunca se elimina.',
    relatedTerms: ['tipo-de-servicio', 'entrada-de-calendario'],
    modules: ['catalog', 'calendar', 'orders'],
  },
  {
    id: 'categoria',
    term: 'Categoría',
    definition:
      'El nombre general que usamos en la Guía para los tipos de servicio, posiciones del menú, etiquetas dietarias y alérgenos: los cuatro se administran juntos en la pantalla Categorías.',
    relatedTerms: ['tipo-de-servicio', 'posicion-del-menu', 'etiqueta-dietaria', 'alergeno'],
    modules: ['catalog'],
  },
  {
    id: 'archivar',
    term: 'Archivar',
    definition:
      'Retirar un menú del catálogo disponible para nuevos pedidos, de forma definitiva. Un menú archivado ya no se puede editar.',
    relatedTerms: ['menu'],
    modules: ['catalog'],
  },
  {
    id: 'precio-base',
    term: 'Precio base',
    definition:
      'El precio de referencia de un menú antes de aplicar el precio específico de un contrato.',
    relatedTerms: ['lista-de-precios'],
    modules: ['catalog', 'contracts'],
  },
  {
    id: 'menu-activo',
    term: 'Activo (menú)',
    definition: 'El menú está disponible para asignarse a contratos y usarse en el calendario.',
    relatedTerms: ['menu-inactivo', 'menu-archivado'],
    modules: ['catalog'],
  },
  {
    id: 'menu-inactivo',
    term: 'Inactivo (menú)',
    definition:
      'El menú está temporalmente deshabilitado, pero no fue archivado: se puede volver a activar.',
    relatedTerms: ['menu-activo'],
    modules: ['catalog'],
  },
  {
    id: 'menu-archivado',
    term: 'Archivado (menú)',
    definition:
      'El menú fue retirado del catálogo de forma definitiva mediante la acción Archivar.',
    relatedTerms: ['archivar'],
    modules: ['catalog'],
  },
  {
    id: 'notas-de-preparacion',
    term: 'Notas de preparación',
    definition:
      'Indicaciones internas para cocina sobre cómo preparar un menú. Solo las ve el personal con acceso a producción.',
    modules: ['catalog'],
  },
  {
    id: 'cupo',
    term: 'Cupo',
    definition:
      'La cantidad máxima de unidades de un menú que se puede reservar para una fecha determinada en el calendario.',
    relatedTerms: ['reserva', 'entrada-de-calendario', 'posicion-del-menu'],
    modules: ['calendar'],
  },
  {
    id: 'reserva',
    term: 'Reserva',
    definition:
      'La cantidad de unidades de un menú que ya está comprometida para una fecha, dentro del cupo disponible.',
    relatedTerms: ['cupo'],
    modules: ['calendar'],
  },
  {
    id: 'entrada-de-calendario',
    term: 'Entrada de calendario',
    definition:
      'La combinación de un menú, una posición y una fecha, con su cupo máximo y su estado de publicación. Solo puede haber un plato por posición y día.',
    relatedTerms: ['cupo', 'publicado', 'posicion-del-menu'],
    modules: ['calendar'],
  },
  {
    id: 'publicado',
    term: 'Publicado',
    definition:
      'Una entrada de calendario publicada ya está visible para que el cliente pida. Antes de publicar es solo un borrador interno.',
    relatedTerms: ['calendario-borrador', 'entrada-de-calendario'],
    modules: ['calendar'],
  },
  {
    id: 'calendario-borrador',
    term: 'Borrador (calendario)',
    definition:
      'Una entrada de calendario todavía no publicada: no es visible para el cliente todavía.',
    relatedTerms: ['publicado'],
    modules: ['calendar'],
  },
  {
    id: 'copiar-semana',
    term: 'Copiar semana',
    definition:
      'Una función que replica la planificación de un rango de fechas a otro rango, para no cargar la misma semana a mano.',
    modules: ['calendar'],
  },
  {
    id: 'advertencia-dietaria',
    term: 'Advertencia dietaria',
    definition:
      'Un aviso que aparece al publicar una fecha si no hay ninguna opción disponible para alguna etiqueta dietaria importante, como sin TACC o vegano. No bloquea la publicación.',
    relatedTerms: ['etiqueta-dietaria', 'publicado'],
    modules: ['calendar'],
  },
  {
    id: 'feriado',
    term: 'Feriado',
    definition: 'Una fecha marcada como excepción en el calendario, con o sin servicio habilitado.',
    relatedTerms: ['feriado-de-zona', 'con-servicio', 'sin-servicio'],
    modules: ['calendar'],
  },
  {
    id: 'feriado-de-zona',
    term: 'Feriado de zona',
    definition:
      'Un feriado que aplica solo a una zona en particular, en vez de a todas. Un feriado de zona tiene prioridad sobre un feriado general de la misma fecha.',
    relatedTerms: ['feriado', 'zona'],
    modules: ['calendar'],
  },
  {
    id: 'con-servicio',
    term: 'Con servicio',
    definition:
      'Un feriado marcado como "con servicio" sigue teniendo entrega ese día, aunque sea feriado.',
    relatedTerms: ['feriado', 'sin-servicio'],
    modules: ['calendar'],
  },
  {
    id: 'sin-servicio',
    term: 'Sin servicio',
    definition: 'Un feriado marcado como "sin servicio" no tiene entrega ese día.',
    relatedTerms: ['feriado', 'con-servicio'],
    modules: ['calendar'],
  },
  {
    id: 'pedido',
    term: 'Pedido',
    definition:
      'La solicitud de menús de una empresa y sede para una fecha de servicio, con sus líneas, cantidades y precios congelados al momento de cargarlo.',
    relatedTerms: ['pedido-borrador', 'pedido-enviado', 'pedido-confirmado'],
    modules: ['orders'],
  },
  {
    id: 'pedido-borrador',
    term: 'Borrador (pedido)',
    definition:
      'El pedido todavía no fue enviado: se puede editar libremente y no cuenta para el corte.',
    relatedTerms: ['pedido', 'pedido-enviado'],
    modules: ['orders'],
  },
  {
    id: 'pedido-enviado',
    term: 'Enviado (pedido)',
    definition:
      'El pedido ya fue enviado, pero todavía se puede editar hasta que se cierre el corte.',
    relatedTerms: ['pedido-borrador', 'pedido-confirmado', 'cierre-de-corte'],
    modules: ['orders'],
  },
  {
    id: 'pedido-confirmado',
    term: 'Confirmado (pedido)',
    definition:
      'El corte se cerró y el pedido quedó congelado: producción ya lo ve. A partir de acá, un cambio se gestiona con una solicitud de cambio.',
    relatedTerms: ['pedido-enviado', 'cierre-de-corte', 'solicitud-de-cambio'],
    modules: ['orders'],
  },
  {
    id: 'cierre-de-corte',
    term: 'Cierre de corte',
    definition:
      'La acción que confirma todos los pedidos enviados y cancela los que quedaron en borrador, para una fecha de servicio. No se puede deshacer.',
    relatedTerms: ['corte', 'pedido-confirmado', 'tablero-del-dia'],
    modules: ['orders'],
  },
  {
    id: 'tablero-del-dia',
    term: 'Tablero del día',
    definition:
      'La pantalla que muestra, para una fecha, qué empresas ya pidieron, cuáles faltan y el estado general antes de cerrar el corte.',
    relatedTerms: ['cierre-de-corte', 'desvio'],
    modules: ['orders'],
  },
  {
    id: 'plantilla-de-pedido',
    term: 'Plantilla de pedido',
    definition:
      'Un pedido recurrente configurado por empresa y sede, con los días de la semana en que aplica y una cantidad por cada posición activa (sin plato: al ser independiente de la fecha, una plantilla no fija qué plato le toca). Sirve para generar pedidos automáticamente en vez de cargarlos a mano cada día.',
    relatedTerms: ['posicion-del-menu'],
    modules: ['orders'],
  },
  {
    id: 'solicitud-de-cambio',
    term: 'Solicitud de cambio',
    definition:
      'El único camino para modificar un pedido que ya está confirmado: alguien pide el cambio, y un usuario con permiso lo aprueba o lo rechaza.',
    relatedTerms: ['pedido-confirmado'],
    modules: ['orders'],
  },
  {
    id: 'desvio',
    term: 'Desvío',
    definition:
      'Una alerta que marca cuando la cantidad pedida se aleja demasiado del promedio habitual de esa empresa y sede.',
    modules: ['orders'],
  },
  {
    id: 'exportar-csv',
    term: 'Exportar CSV',
    definition:
      'Descargar el listado de pedidos o el consolidado de producción como un archivo CSV, para abrirlo en una planilla de cálculo.',
    modules: ['orders', 'production'],
  },
  {
    id: 'planilla-de-produccion',
    term: 'Planilla de producción',
    definition:
      'El consolidado de todo lo que hay que cocinar para una fecha, armado a partir de los pedidos confirmados.',
    relatedTerms: ['planilla-provisoria', 'planilla-cerrada'],
    modules: ['production'],
  },
  {
    id: 'planilla-provisoria',
    term: 'Provisoria',
    definition:
      'La planilla de producción todavía puede cambiar: refleja los pedidos confirmados hasta el momento, pero no está cerrada.',
    relatedTerms: ['planilla-de-produccion', 'planilla-cerrada'],
    modules: ['production'],
  },
  {
    id: 'planilla-cerrada',
    term: 'Cerrada (planilla)',
    definition:
      'La planilla de producción quedó congelada: es la versión definitiva que usa cocina para esa fecha.',
    relatedTerms: ['planilla-de-produccion', 'planilla-provisoria', 'anexo'],
    modules: ['production'],
  },
  {
    id: 'anexo',
    term: 'Anexo',
    definition:
      'Un registro de los cambios en pedidos confirmados que llegaron después de cerrar la planilla de producción. No modifica la planilla original: se suma aparte para que cocina sepa qué cambió.',
    relatedTerms: ['planilla-cerrada', 'solicitud-de-cambio'],
    modules: ['production'],
  },
  {
    id: 'notificacion-in-app',
    term: 'Notificación in-app',
    definition:
      'Un aviso que aparece en la campana de la barra superior y en el listado de "Notificaciones", propio de tu usuario. No leerla no impide que el sistema siga funcionando: es informativa.',
    relatedTerms: ['no-leida', 'preferencia-de-notificacion'],
    modules: ['notifications'],
  },
  {
    id: 'no-leida',
    term: 'No leída',
    definition:
      'Una notificación que todavía no marcaste como leída. Cuenta para el número que muestra la campana.',
    relatedTerms: ['notificacion-in-app'],
    modules: ['notifications'],
  },
  {
    id: 'preferencia-de-notificacion',
    term: 'Preferencia de notificación',
    definition:
      'La elección de cada usuario de recibir o no avisos no críticos por email o por la campana. No afecta a las confirmaciones directas de tus propios pedidos, que siempre se envían.',
    relatedTerms: ['aviso-no-critico', 'notificacion-in-app'],
    modules: ['notifications'],
  },
  {
    id: 'aviso-no-critico',
    term: 'Aviso no crítico',
    definition:
      'Una notificación operativa que no depende de una acción directa tuya (como un recordatorio de corte o un aviso interno). A diferencia de una confirmación de pedido, respeta tus preferencias y podés desactivarla por canal.',
    relatedTerms: ['preferencia-de-notificacion'],
    modules: ['notifications'],
  },
  {
    id: 'rol',
    term: 'Rol',
    definition:
      'El conjunto de permisos que tiene un usuario. Un rol determina qué módulos ves en el menú lateral y qué botones podés usar en cada uno.',
    relatedTerms: ['permiso', 'rol-del-sistema', 'rol-personalizado'],
    modules: ['roles'],
  },
  {
    id: 'permiso',
    term: 'Permiso',
    definition:
      'La autorización puntual para hacer algo, por ejemplo leer contratos o cerrar un corte. Cada rol agrupa un conjunto de permisos.',
    relatedTerms: ['rol'],
    modules: ['roles'],
  },
  {
    id: 'rol-del-sistema',
    term: 'Rol del sistema',
    definition:
      'Uno de los roles predefinidos del catálogo (por ejemplo Administración u Operaciones). Es de solo lectura: no se puede editar ni eliminar desde Roles y Permisos.',
    relatedTerms: ['rol-personalizado', 'rol'],
    modules: ['roles'],
  },
  {
    id: 'rol-personalizado',
    term: 'Rol personalizado',
    definition:
      'Un rol creado a mano con el conjunto de permisos que elijas, para necesidades que ningún rol del sistema cubre. Requiere el permiso "Crear y editar roles y su matriz de permisos" y nunca puede incluir "Crear y editar roles y su matriz de permisos", "Operar como otro usuario (soporte; siempre auditado)" ni "Configurar puntos de venta y credenciales fiscales".',
    relatedTerms: ['rol-del-sistema', 'rol'],
    modules: ['roles'],
  },
  {
    id: 'invitacion',
    term: 'Invitación',
    definition:
      'El email que recibe una persona para activar su cuenta interna: crea su usuario en estado "Invitado" con los roles ya asignados. Queda pendiente hasta que la persona invitada abre el enlace y elige su contraseña.',
    relatedTerms: ['rol'],
    modules: ['users'],
  },
  {
    id: 'entrega-fallida',
    term: 'Entrega fallida',
    definition:
      'Un envío de notificación que agotó sus reintentos automáticos sin lograr entregarse. Visible en la pestaña "Fallidas" para quien tiene el permiso de gestión de notificaciones, con un botón para reintentarla manualmente.',
    relatedTerms: ['notificacion-in-app'],
    modules: ['notifications'],
  },
  {
    id: 'remito',
    term: 'Remito',
    definition:
      'El comprobante que se genera para cada pedido confirmado de una fecha, con las cantidades congeladas. Recorre los estados Pendiente, Despachado, Entregado, Firmado y Facturado, y puede anularse en cualquiera de ellos salvo el último.',
    relatedTerms: ['revision-de-remito', 'remito-firmado', 'anulacion-de-remito'],
    modules: ['delivery-notes'],
  },
  {
    id: 'revision-de-remito',
    term: 'Revisión de remito',
    definition:
      'El número que indica cuántas veces cambiaron las cantidades de un remito después de generado (por ejemplo, por una solicitud de cambio aprobada). Un remito en su versión original tiene revisión 0; el PDF y la pantalla de detalle muestran el mismo número.',
    relatedTerms: ['remito'],
    modules: ['delivery-notes'],
  },
  {
    id: 'remito-firmado',
    term: 'Remito firmado',
    definition:
      'Un remito que ya tiene cargada la firma de recepción: nombre de quien recibió, y opcionalmente su documento y cargo. Si la fecha de firma declarada no coincide con la fecha en que el servidor recibió la carga, queda marcado con "Revisión manual requerida". Puede llegar como un escaneo cargado o como una firma en pantalla; en ambos casos el remito queda "Firmado" y se descarga igual.',
    relatedTerms: ['remito', 'firma-en-pantalla'],
    modules: ['delivery-notes'],
  },
  {
    id: 'firma-en-pantalla',
    term: 'Firma en pantalla',
    definition:
      'Una forma de certificar un remito sin cargar un archivo escaneado: quien recibe firma con el dedo o el mouse en un recuadro dentro del navegador, se completan su nombre y DNI, y al confirmar el remito queda "Firmado" con esa firma embebida en el PDF. Sobre un remito "Despachado" también registra la entrega en el mismo paso ("Registrar entrega y firma").',
    relatedTerms: ['remito', 'remito-firmado'],
    modules: ['delivery-notes'],
  },
  {
    id: 'anulacion-de-remito',
    term: 'Anulación de remito',
    definition:
      'La acción de invalidar un remito, con un motivo obligatorio. Un remito anulado no cuenta para facturación. Un remito ya facturado no admite anulación; para corregirlo, se anula y se genera uno nuevo con los datos correctos.',
    relatedTerms: ['remito'],
    modules: ['delivery-notes'],
  },
  {
    id: 'factura',
    term: 'Factura',
    definition:
      'El comprobante fiscal generado a partir de los remitos firmados de un cliente y un contrato dentro de un mismo período. Recorre los estados Borrador, Enviada a ARCA, Emitida y Rechazada; una vez Emitida, es inmutable.',
    relatedTerms: ['borrador-de-factura', 'cae', 'punto-de-venta'],
    modules: ['billing'],
  },
  {
    id: 'borrador-de-factura',
    term: 'Borrador de factura',
    definition:
      'Una factura recién generada, todavía editable: se pueden excluir remitos con un motivo antes de emitirla. Un cliente con datos fiscales incompletos igual genera su borrador, marcado con una advertencia.',
    relatedTerms: ['factura'],
    modules: ['billing'],
  },
  {
    id: 'cae',
    term: 'CAE',
    definition:
      'El Código de Autorización Electrónico que el organismo fiscal otorga al emitir una factura. Una factura sin CAE (Borrador, Enviada a ARCA o Rechazada) no tiene validez fiscal.',
    relatedTerms: ['factura', 'comprobante-de-prueba'],
    modules: ['billing'],
  },
  {
    id: 'punto-de-venta',
    term: 'Punto de venta',
    definition:
      'El número que identifica ante el organismo fiscal desde dónde se emite un comprobante. En pantalla se llama "PV fiscal (ARCA)". Cada PV fiscal lleva su propia numeración correlativa, sin huecos, por tipo de comprobante. Un "Punto de venta" de la empresa (por ejemplo, una base de operaciones) factura con el PV fiscal al que está vinculado.',
    relatedTerms: ['factura'],
    modules: ['billing'],
  },
  {
    id: 'comprobante-de-prueba',
    term: 'Comprobante de prueba',
    definition:
      'La marca que llevan todas las facturas emitidas mientras el sistema usa el adaptador fiscal de prueba, sin conexión real al organismo. El CAE es simulado; la marca "COMPROBANTE DE PRUEBA — SIN VALIDEZ FISCAL" aparece tanto en la pantalla como en el PDF, y no se puede quitar.',
    relatedTerms: ['factura', 'cae'],
    modules: ['billing'],
  },
  {
    id: 'reconciliar',
    term: 'Reconciliar',
    definition:
      'La acción de consultarle al organismo fiscal el resultado real de una factura que quedó atascada en "Enviada a ARCA" (por ejemplo, si el proceso que la emitía se interrumpió), en vez de reintentar a ciegas.',
    relatedTerms: ['factura'],
    modules: ['billing'],
  },
  {
    id: 'homologacion',
    term: 'Homologación',
    definition:
      'El ambiente de pruebas del organismo fiscal (ARCA): los comprobantes emitidos viajan por la red real pero no tienen validez fiscal. Sirve para probar la configuración antes de pasar a Producción.',
    relatedTerms: ['certificado-digital', 'comprobante-de-prueba'],
    modules: ['fiscal-config'],
  },
  {
    id: 'certificado-digital',
    term: 'Certificado digital',
    definition:
      'El archivo `.crt` que ARCA entrega tras autorizar un pedido de certificado. Se carga por ambiente (Homologación o Producción) y tiene una fecha de vencimiento; sin un certificado vigente para el ambiente activo no se pueden emitir comprobantes ahí.',
    relatedTerms: ['pedido-de-certificado', 'homologacion'],
    modules: ['fiscal-config'],
  },
  {
    id: 'pedido-de-certificado',
    term: 'Pedido de certificado (CSR)',
    definition:
      'El archivo que el sistema genera localmente a partir de los datos del emisor, para presentar ante ARCA y obtener a cambio el certificado digital de un ambiente. Se genera junto con una clave privada que nunca sale del sistema.',
    relatedTerms: ['certificado-digital'],
    modules: ['fiscal-config'],
  },
  {
    id: 'ticket-de-acceso',
    term: 'Ticket de acceso',
    definition:
      'La credencial temporal que ARCA entrega tras iniciar sesión con el certificado digital, necesaria para cualquier llamada posterior a sus servicios. El sistema la obtiene y renueva automáticamente: no requiere ninguna acción manual.',
    modules: ['fiscal-config'],
  },
  {
    id: 'condicion-iva-receptor',
    term: 'Condición frente al IVA del receptor',
    definition:
      'La situación fiscal del cliente que recibe una factura (Responsable Inscripto, Monotributo, Exento o Consumidor Final). El sistema la usa para determinar cómo se presenta el comprobante ante ARCA, sin intervención manual en cada emisión.',
    modules: ['fiscal-config', 'billing'],
  },
  {
    id: 'recibo',
    term: 'Recibo',
    definition:
      'El comprobante que registrás cada vez que un cliente paga: fecha, método, monto recibido y, si corresponde, sus retenciones. Es inmutable una vez registrado; solo se puede anular.',
    relatedTerms: ['imputacion', 'retencion'],
    modules: ['payments'],
  },
  {
    id: 'retencion',
    term: 'Retención',
    definition:
      'Un monto que el cliente descuenta del pago porque está obligado a retenerlo (por ejemplo Ganancias, IVA o Ingresos Brutos) y depositarlo por su cuenta. Se carga como una línea del recibo, con su número de certificado, y suma al total aplicable del recibo.',
    relatedTerms: ['recibo'],
    modules: ['payments'],
  },
  {
    id: 'imputacion',
    term: 'Imputación',
    definition:
      'La acción de aplicar el monto de un recibo a una o más facturas para reducir su saldo pendiente. El sistema sugiere imputar primero las facturas más antiguas, pero el monto de cada línea se puede ajustar antes de confirmar.',
    relatedTerms: ['recibo', 'saldo-a-favor'],
    modules: ['payments'],
  },
  {
    id: 'saldo-a-favor',
    term: 'Saldo a favor',
    definition:
      'El monto de un recibo que quedó sin imputar a ninguna factura. No se pierde: queda disponible como crédito del cliente para una imputación posterior.',
    relatedTerms: ['imputacion'],
    modules: ['payments'],
  },
  {
    id: 'saldo-inicial',
    term: 'Saldo inicial',
    definition:
      'La deuda o el crédito que un cliente ya tenía antes de empezar a usar el sistema, cargada una sola vez para que su cuenta corriente arranque completa desde el primer día. Aparece como el primer movimiento de su cuenta corriente.',
    modules: ['payments'],
  },
  {
    id: 'antiguedad-de-deuda',
    term: 'Antigüedad de deuda',
    definition:
      'La deuda pendiente de un cliente agrupada por cuánto tiempo lleva sin cobrarse, en los tramos Corriente, 1 a 30 días, 31 a 60 días, 61 a 90 días y Más de 90 días. Sirve para priorizar a quién reclamarle el pago primero.',
    relatedTerms: ['factura-vencida'],
    modules: ['payments'],
  },
  {
    id: 'factura-vencida',
    term: 'Factura vencida',
    definition:
      'Una factura emitida con saldo pendiente cuya fecha de vencimiento ya pasó. El sistema la marca automáticamente todas las noches, sin intervención manual.',
    relatedTerms: ['antiguedad-de-deuda'],
    modules: ['payments', 'billing'],
  },
];
